/**
 * <details>の閉じるボタンを管理
 *
 * 仕様
 * - ist-js__detailClose: クリックで親details要素を閉じるボタン
 *
 * 使い方
 * <details>
 *   <summary>開閉ボタン</summary>
 *   コンテンツ
 *   <button class="ist-js__detailClose">閉じる</button>
 * </details>
 */

import {
  DetailAnimationManager,
  DetailAnimation,
} from '../detailsAnimation/_detailsAnimation';

/**
 * 1つのdetails閉じるボタンを管理するクラス
 */
export class DetailClose {
  private closeButton: HTMLElement;
  private parentDetails: HTMLDetailsElement | null;
  private detailAnimation: DetailAnimation | null = null;
  private static mutationObservers: Map<HTMLDetailsElement, MutationObserver> =
    new Map();
  private static observedDetails: Set<HTMLDetailsElement> = new Set();

  constructor(closeButton: HTMLElement) {
    this.closeButton = closeButton;
    this.parentDetails = this.closeButton.closest('details');
    this.init();
  }

  private init(): void {
    if (!this.parentDetails) return;

    // buttonでない場合はrole="button"を追加
    if (this.closeButton.tagName.toLowerCase() !== 'button') {
      this.closeButton.setAttribute('role', 'button');
    }

    // details要素にidが存在しない場合は自動でidを付与
    this.ensureDetailsId();

    // ist-js__detailClose要素にARIA属性を設定
    this.setupCloseButtonAria();

    this.detailAnimation = DetailAnimationManager.getInstance().findAnimation(
      this.parentDetails
    );
    this.closeButton.addEventListener('click', this.handleClick.bind(this));

    // details要素のopen属性の変更を監視
    this.setupMutationObserver();
  }

  private handleClick = (event: Event): void => {
    event.preventDefault();
    event.stopPropagation();
    this.close();
  };

  private ensureDetailsId(): void {
    if (!this.parentDetails) return;

    // details要素にidが存在しない場合は付与
    if (!this.parentDetails.id) {
      // 既存のidと重複しないようにユニークなidを生成
      let uniqueId: string;
      let attempts = 0;
      const maxAttempts = 10;

      do {
        uniqueId = `details-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        attempts++;
      } while (document.getElementById(uniqueId) && attempts < maxAttempts);

      if (attempts < maxAttempts) {
        this.parentDetails.id = uniqueId;
      } else {
        console.warn(
          'DetailClose: ユニークなidの生成に失敗しました',
          this.parentDetails
        );
      }
    }
  }

  private setupCloseButtonAria(): void {
    if (!this.parentDetails) return;

    const detailsId = this.parentDetails.id;
    if (detailsId) {
      // aria-controlsでdetails要素を参照
      this.closeButton.setAttribute('aria-controls', detailsId);

      // aria-expandedで開閉状態を設定
      this.updateCloseButtonAriaExpanded();
    }
  }

  private setupMutationObserver(): void {
    if (!this.parentDetails) return;

    // 既に同じdetails要素が監視されている場合は、新しいMutationObserverを作成しない
    if (DetailClose.observedDetails.has(this.parentDetails)) {
      return;
    }

    try {
      const mutationObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (
            mutation.type === 'attributes' &&
            mutation.attributeName === 'open'
          ) {
            // 同じdetails要素内のすべての閉じるボタンのaria-expandedを更新
            this.updateAllCloseButtonsAriaExpanded();
          }
        });
      });

      mutationObserver.observe(this.parentDetails, {
        attributes: true,
        attributeFilter: ['open'],
      });

      // 静的プロパティに保存
      DetailClose.mutationObservers.set(this.parentDetails, mutationObserver);
      DetailClose.observedDetails.add(this.parentDetails);
    } catch (error) {
      console.error('DetailClose: MutationObserverの設定に失敗しました', error);
    }
  }

  private updateAllCloseButtonsAriaExpanded(): void {
    if (!this.parentDetails) return;

    const isExpanded = this.parentDetails.open;
    const closeButtons = this.parentDetails.querySelectorAll(
      '.ist-js__detailClose'
    );

    closeButtons.forEach((button) => {
      button.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
    });
  }

  private updateCloseButtonAriaExpanded(): void {
    if (!this.parentDetails) return;

    const isExpanded = this.parentDetails.open;
    this.closeButton.setAttribute(
      'aria-expanded',
      isExpanded ? 'true' : 'false'
    );
  }

  private close(): void {
    if (!this.parentDetails) return;

    if (this.detailAnimation) {
      this.detailAnimation.closeWithAnimation();
    } else {
      this.parentDetails.open = false;
    }

    // aria-expandedを更新
    this.updateCloseButtonAriaExpanded();

    const summary = this.parentDetails.querySelector('summary');
    if (summary) {
      summary.focus();
    }
  }

  public destroy(): void {
    this.closeButton.removeEventListener('click', this.handleClick);

    // 同じdetails要素内の他の閉じるボタンが存在するかチェック
    if (this.parentDetails) {
      const remainingCloseButtons = this.parentDetails.querySelectorAll(
        '.ist-js__detailClose'
      );
      const hasOtherCloseButtons = Array.from(remainingCloseButtons).some(
        (button) => button !== this.closeButton
      );

      // 他の閉じるボタンが存在しない場合のみMutationObserverを切断
      if (!hasOtherCloseButtons) {
        const mutationObserver = DetailClose.mutationObservers.get(
          this.parentDetails
        );
        if (mutationObserver) {
          mutationObserver.disconnect();
          DetailClose.mutationObservers.delete(this.parentDetails);
          DetailClose.observedDetails.delete(this.parentDetails);
        }
      }
    }
  }
}

/**
 * 複数のdetails閉じるボタンを管理するマネージャークラス
 */
export class DetailCloseManager {
  private detailCloses: DetailClose[] = [];

  constructor() {
    this.init();
  }

  private init(): void {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.initializeDetailCloses();
      });
    } else {
      this.initializeDetailCloses();
    }
  }

  private initializeDetailCloses(): void {
    const closeButtons = document.querySelectorAll('.ist-js__detailClose');
    closeButtons.forEach((button) => {
      this.detailCloses.push(new DetailClose(button as HTMLElement));
    });
  }

  public getDetailCloses(): DetailClose[] {
    return this.detailCloses;
  }

  public destroy(): void {
    this.detailCloses.forEach((detailClose) => detailClose.destroy());
    this.detailCloses = [];
  }
}
